import api from './baseApi';

/**
 * The circle "360" tabs: what 360review knows about the circle's members, and
 * the account owner's admin session. All routes exist only for circles with
 * `integrations.review360`.
 *
 * `dashboardUrl` is a login credential for the member's own 360review
 * dashboard and `adminUrl` a one-hour credential for the admin dashboard.
 * Keep them in component state only: never in Redux, storage or logs.
 */

export type Review360RoundStatus = 'collecting' | 'unlocked' | 'closed';

export interface Review360Round {
  ordinal: number;
  status: Review360RoundStatus;
  respondents: number;
  unlockAt?: number;
  openedAt?: string | null;
  unlockedAt?: string | null;
}

export type Review360Me =
  | {
      found: true;
      name: string;
      dashboardUrl: string;
      teamLink: string | null;
      round: Review360Round;
    }
  | { found: false; reason?: 'no_email' };

export interface Review360ParticipantStatus {
  email: string | null;
  found: boolean;
  name?: string;
  round?: Pick<Review360Round, 'ordinal' | 'status' | 'respondents'> & {
    unlockAt?: number;
  };
}

export interface Review360Participants {
  seats: { purchased: number; remaining: number } | null;
  /** Keyed by the participant's user id. */
  results: Record<string, Review360ParticipantStatus>;
}

/**
 * The leader's 360review admin session (docs/briefs/oth-360-admin-tab.md).
 * Granted only when the leader's email is the account owner's.
 */
export type Review360AdminSession =
  | { granted: true; adminUrl: string; expiresAt: string }
  | { granted: false; reason: 'not_owner' | 'no_email' };

type ApiEnvelope<T> = { result: T };

export const REVIEW360_ADMIN_URL = 'https://www.360review.io/admin';

export const review360Api = {
  /** The caller's own 360 in the circle's 360review account. */
  getMy360: async (circleId: string): Promise<Review360Me> => {
    const { data } = await api.get<ApiEnvelope<Review360Me>>(
      `tribes/${encodeURIComponent(circleId)}/360/me`
    );
    return data.result;
  },

  /**
   * For the circle leader who owns the 360review account: a one-hour admin
   * session. Ask only when the tab is opened; the URL is used once, in a frame.
   */
  getAdminSession: async (circleId: string): Promise<Review360AdminSession> => {
    const { data } = await api.get<ApiEnvelope<Review360AdminSession>>(
      `tribes/${encodeURIComponent(circleId)}/360/admin`
    );
    return data.result;
  },

  /** For circle leaders: who among the participants has a 360, and the seats left. */
  getParticipants: async (circleId: string): Promise<Review360Participants> => {
    const { data } = await api.get<ApiEnvelope<Review360Participants>>(
      `tribes/${encodeURIComponent(circleId)}/360/participants`
    );
    return data.result;
  },
};
